import * as React from "react";
import * as ReactDOM from "react-dom";

interface IProperties
{
    /**
     * Enthält eine Funktion die dazu da ist um ein Item zu rendern.
     */
    children: (index: number, style: any) => JSX.Element;

    /**
     * Die Breite der Liste.
     */
    width: number | string;

    /**
     * Die Höhe der Liste.
     */
    height: number | string;

    /**
     * Die komplette Anzahl der Items.
     */
    itemCount: number;

    /**
     * Die Höhe eines einzelnen Items.
     */
    itemHeight: number;

    /**
     * Der Index zu dem gescrollt werden soll.
     */
    scrollToIndex?: number;

    onScroll?: (e: any) => void;
}

interface IState
{
    /**
     * Die momentane Scroll Position.
     */
    currentPosition: number,

    /**
     * Enthält das DOM-Element des Containers der @see VirtualList Komponente.
     */
    containerRef?: HTMLDivElement
}

export class VirtualList extends React.PureComponent<IProperties, IState>
{
    /**
     * Enthält die Anzahl der sichtbaren Items.
     */
    private _screenItemsLen: number;

    /**
     * Enthält die Anzahl der sichtbaren Items plus cache.
     */
    private _cachedItemsLen: number;

    /**
     * Enthält den zuletzt verwendeten scrollTop Wert.
     */
    private _lastRepaintY: number;

    /**
     * Gibt an, wann der nächste Chunk gerendert wird.
     */
    private _maxBuffer: number;

    /**
     * Initialisiert die Komponente @see VirtualList.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Die ersten Items rendern.
        this.state = {
            currentPosition: 0
        };

        this.setProps(props, this.state);
    }

    componentWillUpdate(nextProps, nextState)
    {
        this.setProps(nextProps, nextState);
    }

    componentDidUpdate()
    {
        if (this.props.scrollToIndex != null)
        {
            var index: number = this.props.scrollToIndex - 1
            index = index < 0 ? 0 : index;
            var position: number = index * this.props.itemHeight;

            if (this.state.containerRef != null)
                this.state.containerRef.scrollTop = position;
        }
    }

    private setProps(props: IProperties, state: IState)
    {
        const { height, itemHeight } = props;
        const { containerRef } = state;
        var containerHeight: number = 0;

        if (height === "100%" && containerRef != null)
            containerHeight = containerRef.offsetHeight;
        else if (height === "100%")
            containerHeight = 0;
        else
            containerHeight = height as number;
        
        // Die Anzahl ermitteln die im sichtbaren Bereich der Liste dargestellt werden kann.
        this._screenItemsLen = Math.ceil(containerHeight / itemHeight);

        // Die Anzahl der gecachten Items erhöhen damit das Scrollen natürlicher wirkt.
        this._cachedItemsLen = this._screenItemsLen * 3;
        this._maxBuffer = this._screenItemsLen * itemHeight;
    }

    /**
     * Ermittelt/Rendert die momentan sichtbaren Items.
     * @param from Der Index ab dem gerendert werden soll.
     */
    private renderChunk(from: number): JSX.Element[]
    {
        var finalItem = from + this._cachedItemsLen;
        if (finalItem > this.props.itemCount)
            finalItem = this.props.itemCount;

        var children: JSX.Element[] = [];

        for (var i = from; i < finalItem; i++)
        {
            children.push(this.props.children(i, {
                position: "absolute",
                top: (i * this.props.itemHeight)
            }));
        }

        return children;
    }

    /**
     * Wird ausgeführt sobald in der Liste gescrollt wird.
     * @param e
     */
    private onScroll = (e) =>
    {
        var scrollTop: number = e.target != null ? e.target.scrollTop : e;

        if (!this._lastRepaintY || Math.abs(scrollTop - this._lastRepaintY) > this._maxBuffer)
        {
            var first = parseInt((scrollTop / this.props.itemHeight).toString()) - this._screenItemsLen;
            this.setState({
                currentPosition: first < 0 ? 0 : first
            }, () => this._lastRepaintY = scrollTop);
        }
        
        e.preventDefault && e.preventDefault();
        this.props.onScroll && this.props.onScroll(e);
    }

    /**
     * Speichert das DOM-Element der VirtualList ins State zur weiteren Verwendung.
     * @param ref
     */
    private setRef = (ref: HTMLDivElement) =>
    {
        this.setState({ containerRef: ref });
    }

    /**
     * Diese Scheiße muss gemacht werden. Weil wenn es nicht gemacht wird, erscheint immer
     * eine horizontale ScrollBar die garnicht benötigt wird.
     * @param ref
     */
    private setScrollerRef = (ref: HTMLDivElement) =>
    {
        if (ref != null)
        {
            setTimeout(() =>
            {
                ref.style.left = "1px";
            }, 50);
        }
    }

    /**
     * Rendert diese Komponente.
     */
    render()
    {
        const virtualListStyle = {
            width: this.props.width,
            height: this.props.height
        };
        
        const scrollerStyle = {
            opacity: 0,
            top: 0,
            left: 0,
            width: 1,
            height: this.props.itemHeight * this.props.itemCount
        };

        const items = this.renderChunk(this.state.currentPosition);

        return (
            <div
                ref={this.setRef}
                onScroll={this.onScroll}
                style={{
                    position: "relative",
                    overflow: "auto",
                    ...virtualListStyle
                }}>
                <div
                    ref={this.setScrollerRef}
                    style={{
                        position: "absolute",
                        ...scrollerStyle
                    }}></div>
                {items}
            </div>
        );
    }
}